import { useEffect, useRef } from 'react'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { useExternalLinkIframe } from '../../state'

// Domains that block iframe embedding (X-Frame-Options: DENY/SAMEORIGIN)
const IFRAME_BLOCKED_DOMAINS = ['www.afterpay.com', 'help.afterpay.com', 'portal.afterpay.com']

const canOpenInIframe = (href: string): boolean => {
  try {
    const url = new URL(href, window.location.href)
    return !IFRAME_BLOCKED_DOMAINS.some((domain) => url.hostname === domain || url.hostname.endsWith(`.${domain}`))
  } catch {
    return true
  }
}

export const GlobalLinkInterceptor = () => {
  const { open, isOpen } = useExternalLinkIframe()
  const lastTriggerElement = useRef<HTMLAnchorElement | null>(null)

  useEffect(() => {
    const interceptLink = (link: HTMLAnchorElement, e: Event) => {
      const href = link.getAttribute('href')
      const isExternal = link.target === '_blank'

      if (isExternal && href !== null) {
        e.preventDefault()

        if (canOpenInIframe(href)) {
          lastTriggerElement.current = link
          // Make sure the link is opened in the next tick to avoid race conditions with rendering cycle
          setTimeout(() => open(href), 0)
        } else {
          // Open in new tab for domains that block iframes
          window.location.href = href
        }
      }
    }

    const getExternalLink = (e: Event) => {
      const target = e.target as HTMLElement
      const link = target.closest('a')
      return link?.target === '_blank' ? link : null
    }

    const handleClick = (e: MouseEvent) => {
      const link = getExternalLink(e)
      if (valueIsNotNullOrUndefined(link)) interceptLink(link, e)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      const link = getExternalLink(e)
      if (valueIsNotNullOrUndefined(link)) interceptLink(link, e)
    }

    document.addEventListener('click', handleClick, true)
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [open])

  useEffect(() => {
    if (!isOpen && lastTriggerElement.current) {
      lastTriggerElement.current.focus()
      lastTriggerElement.current = null
    }
  }, [isOpen])

  return null
}
